import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { track } from '@/api/tracking'
import { LeadForm, type LeadFormSuccessResult } from '@/components/LeadForm'
import { LeadFormOfferSuccess } from '@/components/LeadFormOfferSuccess'
import { FoodCostAuthoritySection } from '@/components/food-cost/FoodCostAuthoritySection'
import { FoodCostHeader } from '@/components/food-cost/FoodCostHeader'
import { FoodCostHero } from '@/components/food-cost/FoodCostHero'
import { FoodCostSideCharacter } from '@/components/food-cost/FoodCostSideCharacter'
import { FOOD_COST_LANDING_PAGE, FORM_HASH } from '@/components/food-cost/constants'
import { NewsletterSubscribe } from '@/features/newsletter/NewsletterSubscribe'
import { parseQuizAnswers, resolveRecommendationReason } from '@/features/router-landing/quiz'
import { cn } from '@/lib/utils'
import { ROUTES, SITE_ORIGIN } from '@/navigation/constants'
import { localizePath } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import '@/styles/food-cost-fonts.css'

import { ViasWhatIsTipiSection, ViasWhyWithinReachSection } from './ViasContextSections'
import { ViasOfferSection } from './ViasOfferSection'
import {
    OFFER_PATH_FORM_OPTIONS,
    type OfferPathId,
    RECOMMENDED_PLAN_PARAM,
    type RecommendedPlanId,
    VIAS_LANDING_VARIANT,
    getOfferPathLeadConcern,
    parseRecommendedPlan,
} from './offerPaths'

const VIAS_HASH = '#vias'
const OG_IMAGE = `${SITE_ORIGIN}/escandallo-restaurante.webp`
const VIAS_MIXPANEL_CONTEXT = { landing_variant: VIAS_LANDING_VARIANT } as const

const FORM_FIELD_CLASS =
    'h-[54px] min-w-0 w-full rounded-t-[6px] rounded-b-none border-0 border-b-2 border-[#241A12] bg-[rgba(36,26,18,0.08)] px-4 text-[15px] font-medium leading-none text-[#5C5548] shadow-none placeholder:text-[#8A7F6F] focus-visible:bg-[rgba(240,79,33,0.1)] focus-visible:border-b-[#F04F21] focus-visible:ring-0'

function scrollToHash(hash: string) {
    document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' })
}

function ViasFinalCta({
    selectedPath,
    recommendedPlanId,
    onSelectPath,
}: {
    selectedPath: OfferPathId
    recommendedPlanId?: RecommendedPlanId
    onSelectPath: (pathId: OfferPathId) => void
}) {
    const formRef = useRef<HTMLDivElement>(null)
    const hasTrackedFormView = useRef(false)
    const [offerSuccess, setOfferSuccess] = useState<LeadFormSuccessResult | null>(null)
    const mixpanelContext = {
        landing_variant: VIAS_LANDING_VARIANT,
        offer_path: selectedPath,
        recommended_plan: recommendedPlanId,
    }

    useEffect(() => {
        const el = formRef.current
        if (!el || hasTrackedFormView.current) return
        const observer = new IntersectionObserver(
            entries => {
                if (!entries.some(entry => entry.isIntersecting) || hasTrackedFormView.current) return
                hasTrackedFormView.current = true
                track('Form', {
                    landing_page: FOOD_COST_LANDING_PAGE,
                    landing_variant: VIAS_LANDING_VARIANT,
                    offer_path: selectedPath,
                    recommended_plan: recommendedPlanId,
                })
            },
            { threshold: 0.2 },
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [selectedPath, recommendedPlanId])

    if (offerSuccess) {
        return (
            <section className="bg-[#F5EEE4] pb-8 pt-2 font-['Archivo',system-ui,sans-serif] md:pb-12">
                <div id='form' className='scroll-mt-24'>
                    <LeadFormOfferSuccess
                        leadId={offerSuccess.leadId}
                        createdAt={offerSuccess.createdAt}
                        landingPage={FOOD_COST_LANDING_PAGE}
                        mixpanelContext={mixpanelContext}
                    />
                </div>
            </section>
        )
    }

    return (
        <section className="bg-[#F5EEE4] pb-3 pt-1 font-['Space_Mono',monospace] md:pb-6">
            <div
                id='form'
                ref={formRef}
                className='mx-auto flex max-w-[1180px] scroll-mt-20 flex-col gap-4 px-3 pt-8 md:gap-5 md:px-6 md:pt-12'
            >
                <div className='grid grid-cols-1 gap-8 rounded-[28px] bg-[#17100A] px-4 pb-4 pt-4 md:grid-cols-[minmax(0,0.88fr)_minmax(390px,0.82fr)] md:px-6 md:py-6'>
                    <div className='flex flex-col justify-between gap-6 px-2 pt-2 md:pr-4'>
                        <div className='flex flex-col gap-4'>
                            <h2 className="m-0 max-w-[14ch] font-['Archivo',system-ui,sans-serif] text-[34px] font-bold leading-[1.12] tracking-[-1px] text-[#F5EEE4] md:text-[52px] md:tracking-[-2px]">
                                Dinos cómo quieres ponerlo en orden
                            </h2>
                            <p className="m-0 max-w-[34ch] font-['Archivo',system-ui,sans-serif] text-[15px] leading-[1.7] text-[#C9C0B3] md:text-[17px]">
                                Te llamamos. Revisamos el plan y, en la misma llamada, podemos hacer el escandallo de un
                                plato de tu carta.
                            </p>
                        </div>
                        <div className="hidden font-['Archivo',system-ui,sans-serif] md:flex md:flex-col md:gap-3 md:text-sm md:text-[#C9C0B3]">
                            <div>Escandallo real con ingredientes y precios de tu negocio.</div>
                            <div>Sin compromiso.</div>
                        </div>
                    </div>

                    <div className='text-[#241A12]' style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,.5))' }}>
                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom]'
                            aria-hidden='true'
                        />
                        <div className='bg-[#f4eee3] px-5 pb-5 pt-6 md:px-6 md:pb-6'>
                            <div className='border-b-0 border-t-0 pb-4 text-center'>
                                <div className='text-[12px] font-bold uppercase tracking-[0.3em] text-[#241A12]'>
                                    * TIPI *
                                </div>
                                <div className='mt-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[rgba(36,26,18,0.45)]'>
                                    ELIGE TU PLAN
                                </div>
                            </div>

                            <fieldset className="m-0 border-0 border-t-2 border-dashed border-t-[rgba(36,26,18,0.3)] p-0 pb-4 pt-4 font-['Archivo',system-ui,sans-serif]">
                                <legend className='sr-only'>Una opción para cada situación</legend>
                                <div className='flex flex-col gap-2'>
                                    {OFFER_PATH_FORM_OPTIONS.map(option => {
                                        const checked = selectedPath === option.id
                                        return (
                                            <label
                                                key={option.id}
                                                className={cn(
                                                    'flex cursor-pointer items-start gap-3 rounded-[12px] border px-3 py-2.5 text-[13px] leading-[1.35] transition-colors md:text-[14px]',
                                                    checked
                                                        ? 'border-[#F4502A] bg-[rgba(244,80,42,0.08)] text-[#241A12]'
                                                        : 'border-[rgba(36,26,18,0.15)] text-[#5C5548] hover:border-[rgba(36,26,18,0.35)]',
                                                )}
                                            >
                                                <input
                                                    type='radio'
                                                    name='offer_path'
                                                    value={option.id}
                                                    checked={checked}
                                                    onChange={() => onSelectPath(option.id)}
                                                    className='mt-0.5 size-4 shrink-0 accent-[#D13D1A]'
                                                />
                                                <span>{option.label}</span>
                                            </label>
                                        )
                                    })}
                                </div>
                            </fieldset>

                            <div className='border-t-2 border-dashed border-t-[rgba(36,26,18,0.3)] pt-4 [&_[data-slot=checkbox-indicator]]:text-[#f4eee3] [&_[data-slot=checkbox][data-state=checked]]:border-[#241A12] [&_[data-slot=checkbox][data-state=checked]]:bg-[#241A12] [&_[data-slot=checkbox]]:mt-0 [&_[data-slot=checkbox]]:size-6 [&_[data-slot=checkbox]]:rounded-[4px] [&_[data-slot=checkbox]]:border-[#8E8476] [&_[data-slot=checkbox]]:bg-transparent [&_a]:font-bold [&_a]:text-[#C54A2C] [&_a]:underline [&_form]:space-y-5 [&_label]:text-[#241A12] [&_p]:text-[#5C5548]'>
                                <LeadForm
                                    landingPage={FOOD_COST_LANDING_PAGE}
                                    mixpanelContext={mixpanelContext}
                                    mainConcern={getOfferPathLeadConcern(selectedPath)}
                                    showIntroOfferOnSuccess={false}
                                    onSuccess={result => {
                                        if (result.createdAt) setOfferSuccess(result)
                                    }}
                                    emphasizePrimaryCta
                                    defaultCallIntent='schedule'
                                    submitButtonText='Quiero que me llaméis'
                                    fieldClassName={FORM_FIELD_CLASS}
                                    nameFieldLabel='01 · Nombre'
                                    phoneFieldLabel='02 · Móvil'
                                    roleFieldLabel='03 · Rol'
                                    fieldLabelClassName='text-[rgba(36,26,18,0.65)]'
                                    consentSectionClassName='pt-4 opacity-100'
                                    consentLabelClassName='text-[13px] leading-[1.7] text-[#241A12]'
                                    consentSubtextClassName='text-[11px] leading-[1.8] text-[#5C5548]'
                                    submitButtonClassName='flex h-auto min-h-[54px] items-center justify-center gap-2 whitespace-normal text-balance rounded-[12px] border-none bg-[#D13D1A] px-4 py-[18px] text-[15px] font-bold uppercase tracking-[0.12em] text-white shadow-none hover:bg-[#C03214]'
                                    formErrorClassName='text-[#C54A2C]'
                                />
                            </div>
                        </div>
                        <div
                            className='h-3 bg-[linear-gradient(-45deg,#f4eee3_6px,transparent_0),linear-gradient(45deg,#f4eee3_6px,transparent_0)] bg-[length:12px_12px] bg-repeat-x [background-position:bottom] [transform:scaleY(-1)]'
                            aria-hidden='true'
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}

export function ViasAcompanamientoPage() {
    const locale = useLandingLocale()
    const pagePath = localizePath(ROUTES.ponloEnOrden, locale)
    const heroRef = useRef<HTMLElement>(null)
    const howItWorksRef = useRef<HTMLDivElement>(null)
    const characterSectionsRef = useRef<HTMLDivElement>(null)
    const [headerProgress, setHeaderProgress] = useState(0)
    const [searchParams] = useSearchParams()
    // Set by the /empieza quiz; read once so in-page choices are not overridden.
    const [{ recommendedPlan, recommendationReason }] = useState(() => {
        const plan = parseRecommendedPlan(searchParams.get(RECOMMENDED_PLAN_PARAM))
        return {
            recommendedPlan: plan,
            recommendationReason: plan ? resolveRecommendationReason(parseQuizAnswers(searchParams), plan) : null,
        }
    })
    const [selectedPath, setSelectedPath] = useState<OfferPathId>(recommendedPlan ?? 'local_rentable')

    const title = 'Tienes los números abandonados | Tipi'
    const description =
        'Tienes los números abandonados. Tipi Express, Local Rentable, apaga el incendio, o Asesoría Tipi si quieres olvidarte de los números.'

    useEffect(() => {
        const onScroll = () => {
            const howItWorksTop = howItWorksRef.current?.getBoundingClientRect().top
            if (typeof howItWorksTop !== 'number') return
            const fadeDistance = window.innerHeight * 0.2
            setHeaderProgress(Math.max(0, Math.min(1, (fadeDistance - howItWorksTop) / fadeDistance)))
        }

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const onHash = () => {
            const hash = window.location.hash
            if (hash === FORM_HASH || hash === VIAS_HASH) scrollToHash(hash)
        }
        onHash()
        window.addEventListener('hashchange', onHash)
        return () => window.removeEventListener('hashchange', onHash)
    }, [])

    const goToHash = (hash: string) => {
        const base = pagePath === '/' ? '' : pagePath
        window.history.replaceState(null, '', `${base}${hash}`)
        scrollToHash(hash)
    }

    const selectPath = (pathId: OfferPathId) => {
        setSelectedPath(pathId)
        track('Offer Path Selected', {
            landing_page: FOOD_COST_LANDING_PAGE,
            landing_variant: VIAS_LANDING_VARIANT,
            offer_path: pathId,
        })
    }

    const selectPathAndForm = (pathId: OfferPathId) => {
        selectPath(pathId)
        goToHash(FORM_HASH)
    }

    return (
        <div className="min-h-screen overflow-x-hidden bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta name='robots' content='noindex,nofollow' />
                <meta property='og:title' content={title} />
                <meta property='og:description' content={description} />
                <meta property='og:image' content={OG_IMAGE} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
            </PageHelmet>

            <div className='mx-auto w-full overflow-x-hidden'>
                <FoodCostHeader
                    onCtaClick={() => goToHash(FORM_HASH)}
                    progress={headerProgress}
                    landingPage={FOOD_COST_LANDING_PAGE}
                    mixpanelContext={VIAS_MIXPANEL_CONTEXT}
                    ctaLabel='Elige tu plan'
                />
                <main className='overflow-x-hidden'>
                    <FoodCostHero
                        ref={heroRef}
                        onCtaClick={() => goToHash(VIAS_HASH)}
                        landingPage={FOOD_COST_LANDING_PAGE}
                        mixpanelContext={VIAS_MIXPANEL_CONTEXT}
                        title='Tienes los números abandonados'
                        subtitle='Es normal priorizar a tus clientes por encima de la gestión.'
                        answerCapsule='Un negocio se saca adelante currando el día a día pero también controlando tus números, te dejamos elegir cómo.'
                        ctaLabel='Elige cómo lo arreglas'
                    />
                    <ViasWhatIsTipiSection />
                    <div ref={characterSectionsRef}>
                        <div ref={howItWorksRef} data-character-section data-foodcost-section='2'>
                            <ViasWhyWithinReachSection />
                        </div>
                        <div data-character-section>
                            <ViasOfferSection
                                recommendedPlan={recommendedPlan}
                                recommendationReason={recommendationReason}
                                onSelectPath={selectPathAndForm}
                            />
                        </div>
                        <div data-character-section>
                            <FoodCostAuthoritySection
                                landingPage={FOOD_COST_LANDING_PAGE}
                                mixpanelContext={VIAS_MIXPANEL_CONTEXT}
                            />
                        </div>
                        <div data-character-section>
                            <ViasFinalCta
                                selectedPath={selectedPath}
                                recommendedPlanId={recommendedPlan ?? undefined}
                                onSelectPath={selectPath}
                            />
                        </div>
                        <div data-character-section>
                            <NewsletterSubscribe variant='landing' source='landing/ponlo-en-orden' />
                        </div>
                        <div>
                            <Footer landingPage='food-cost' className='mt-3 border-[#F5EEE4]/10 bg-[#17130E]' />
                        </div>
                    </div>
                </main>
                <FoodCostSideCharacter sectionRef={characterSectionsRef} heroRef={heroRef} />
            </div>
        </div>
    )
}
